import { History } from "lucide-react";
import moment from "moment/moment";
import * as React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog } from "@/components/ui";
import { statusDisplayLabel } from "@/components/VideoProjectStatusPicker";
import { AasmEventLog, AasmEventLogServices, VideoProject } from "@/services/bedrock";
import { useSocialCoachSession } from "@/utils";

const videoProjectEventLogsQueryKey = (id: number | string) => ["videoProjectEventLogs", String(id)] as const;

const PREVIEW_LIMIT = 5;

const EVENT_LABELS: Record<string, string> = {
  create: "created the project",
  start_editing: "started editing",
  stop_editing: "stopped editing",
  submit_for_testing: "submitted project for testing",
  complete: "completed",
  return_to_editing: "returned project to editing",
  start_testing: "started testing",
  stop_testing: "stopped testing",
  reject_qa: "rejected QA",
  approve_qa: "approved QA",
  request_revision: "requested a revision",
  schedule: "scheduled for posting",
  fail_post: "marked the post as failed",
  succeed_post: "marked the post as successful",
  assign: "updated the assigned editor or QA tester",
  upload_media: "uploaded media",
  archive: "archived the project",
  duplicate: "duplicated the project",
};

interface VideoProjectHistoryPanelProps {
  videoProjectId: number;
}

function formatStatusValue(value?: string | null, revealInternalStatuses = true): string {
  if (!value) return "Unknown";
  return statusDisplayLabel(value as VideoProject.Status, revealInternalStatuses);
}

function HistoryRow({ item, revealInternalStatuses }: { item: AasmEventLog; revealInternalStatuses: boolean }) {
  const failed = item.succeeded === false;
  const actorName = item.actorName || "System";
  const eventLabel = item.event ? EVENT_LABELS[item.event] || item.event.replace(/_/g, " ") : "Event";
  const statusChanged = Boolean(item.toStatus) && item.fromStatus !== item.toStatus;
  const actor = <span className="font-bold">{actorName}</span>;
  const headline = statusChanged ? (
    <>
      {actor} transitioned from{" "}
      <span className="font-bold">{formatStatusValue(item.fromStatus, revealInternalStatuses)}</span> to{" "}
      <span className="font-bold">{formatStatusValue(item.toStatus, revealInternalStatuses)}</span>
    </>
  ) : (
    <>
      {actor} {eventLabel}
    </>
  );

  return (
    <div className="flex flex-col gap-0.5 py-1.5 border-b border-slate-100 last:border-b-0 last:pb-0 first:pt-0">
      <div className="flex flex-row justify-between items-baseline gap-2">
        <span className={`text-xs font-medium leading-tight ${failed ? "text-rose-700" : "text-slate-700"}`}>
          {failed ? (
            <>
              Failed: {actor} {eventLabel}
            </>
          ) : (
            headline
          )}
        </span>
        <span className="text-slate-400 text-[11px] font-medium shrink-0 leading-tight">
          {item.createdDate ? moment(item.createdDate).format("MMM D, h:mm a") : ""}
        </span>
      </div>
      {failed && item.error && <span className="text-[11px] text-rose-600 leading-tight">{item.error}</span>}
    </div>
  );
}

export function VideoProjectHistoryPanel({ videoProjectId }: VideoProjectHistoryPanelProps) {
  const { isAdmin } = useSocialCoachSession();
  const [showAllOpen, setShowAllOpen] = React.useState(false);
  const { data: eventLogs = [], isLoading } = useQuery({
    queryKey: videoProjectEventLogsQueryKey(videoProjectId),
    queryFn: () => AasmEventLogServices.index(videoProjectId),
  });

  const previewLogs = eventLogs.slice(0, PREVIEW_LIMIT);
  const hasMore = eventLogs.length > PREVIEW_LIMIT;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row justify-between items-center w-full select-none">
        <label className="sectionHeader font-semibold text-slate-800 text-sm flex items-center gap-1.5">
          <History className="size-4.5 text-blue-600" />
          History
        </label>
        {hasMore && (
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-100 font-semibold"
            onClick={() => setShowAllOpen(true)}
          >
            View all
          </Button>
        )}
      </div>
      <div className="videoProjectContainer topContainer flex flex-col bg-slate-50/50 px-4 py-3 rounded-xl border border-slate-200">
        {isLoading && <span className="text-xs text-slate-400">Loading history…</span>}
        {!isLoading && eventLogs.length === 0 && <span className="text-xs text-slate-400">No status events yet.</span>}
        {previewLogs.map((item, index) => (
          <HistoryRow key={item.id ?? `${item.createdDate}-${index}`} item={item} revealInternalStatuses={isAdmin} />
        ))}
      </div>

      <Dialog open={showAllOpen} onOpenChange={setShowAllOpen} size="md">
        <Dialog.Header>
          <Dialog.Title className="flex items-center gap-1.5 text-slate-800 text-base font-bold">
            <History className="size-5 text-blue-600" />
            History
          </Dialog.Title>
        </Dialog.Header>
        <Dialog.Content className="p-4 max-h-[60vh] overflow-y-auto flex flex-col">
          {eventLogs.map((item, index) => (
            <HistoryRow key={item.id ?? `${item.createdDate}-${index}`} item={item} revealInternalStatuses={isAdmin} />
          ))}
        </Dialog.Content>
        <Dialog.Footer showCloseButton />
      </Dialog>
    </div>
  );
}

export function useInvalidateVideoProjectEventLogs() {
  const queryClient = useQueryClient();
  return (id: number | string) => queryClient.invalidateQueries({ queryKey: videoProjectEventLogsQueryKey(id) });
}
